import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAccountantAuth } from '../contexts/AccountantAuthContext';
import { apiFetch } from '../utils/api';
import {
  Home,
  FileText,
  Bell,
  ChevronLeft,
  ChevronRight,
  LogOut,
  User,
  Menu,
  X,
  MessageSquare,
  PlusCircle,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Mail
} from 'lucide-react';

interface AccountantLayoutProps {
  children: React.ReactNode;
}

export default function AccountantLayout({ children }: AccountantLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { accountant, logout } = useAccountantAuth();
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationCount, setNotificationCount] = useState(0);

  // Fetch notifications (pending invites + unread comments)
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!accountant) return;
      
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('accountant_token')}` };
        
        const notifs: any[] = [];
        
        // Fetch pending invites using api utility
        try {
          const invites = await apiFetch<any[]>(
            '/api/v1/collaboration/invites/pending',
            { headers, showErrorToast: false }
          );
          invites.forEach((invite: any) => {
            notifs.push({
              id: `invite-${invite.report_id}`,
              type: 'invite',
              title: 'New Report Shared',
              message: `${invite.shared_by_name} shared ${invite.client_name}`,
              time: new Date(invite.shared_at),
              reportId: invite.report_id
            });
          });
        } catch {
          // Silent fail for notifications
        }
        
        // Fetch dashboard stats for unread comments count
        try {
          const stats = await apiFetch<{ unread_comments: number }>(
            '/api/v1/collaboration/accountant/dashboard-stats',
            { headers, showErrorToast: false }
          );
          if (stats.unread_comments > 0) {
            notifs.push({
              id: 'unread-comments',
              type: 'comment',
              title: 'Unread Comments',
              message: `You have ${stats.unread_comments} unread comment${stats.unread_comments > 1 ? 's' : ''}`,
              time: new Date(),
              path: '/accountant/comments'
            });
          }
        } catch {
          // Silent fail for notifications
        }
        
        notifs.sort((a, b) => b.time.getTime() - a.time.getTime());
        setNotifications(notifs);
        setNotificationCount(notifs.length);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };
    
    fetchNotifications();
  }, [accountant]);

  const navItems = [
    { icon: Home, label: 'Dashboard', path: '/queue/accountant' },
    { icon: FileText, label: 'Shared Reports', path: '/accountant/reports' },
    { icon: MessageSquare, label: 'Comments', path: '/accountant/comments' },
  ];
  
  const bottomNavItems = [
    { icon: User, label: 'Profile', path: '/accountant/profile' },
  ];

  const isActive = (path: string) => {
    if (path === '/queue/accountant') return location.pathname === '/queue/accountant' || location.pathname === '/accountant' || location.pathname === '/accountant/dashboard';
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/accountant/login');
  };

  const Sidebar = ({ mobile = false }: { mobile?: boolean }) => (
    <div className={`
      ${mobile ? 'w-64' : sidebarCollapsed ? 'w-16' : 'w-60'}
      bg-[#0b2545] text-white transition-all duration-300 flex flex-col h-full
    `}>
      {/* Logo */}
      <div className="p-4 border-b border-white/10">
        <div 
          className="flex items-center justify-center cursor-pointer"
          onClick={() => navigate('/queue/accountant')}
          title="Go to Dashboard"
        >
          {sidebarCollapsed && !mobile ? (
            <img 
              src="/logo.svg" 
              alt="ClaimSync" 
              className="h-10 w-10 object-contain hover:opacity-80 transition-opacity"
            />
          ) : (
            <div className="flex flex-col items-center">
              <img 
                src="/logo.svg" 
                alt="ClaimSync" 
                className="h-44 w-auto max-w-[220px] object-contain hover:opacity-80 transition-opacity"
              />
              <span className="text-xs text-blue-300 -mt-2">Accountant Portal</span>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-2">
        {/* Create New Claim - Prominent Link to Main Dashboard */}
        <div className="mb-4">
          <a
            href="/wizard"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-600/30 hover:from-blue-500 hover:to-blue-400"
          >
            <PlusCircle className="w-[18px] h-[18px] flex-shrink-0" />
            {(!sidebarCollapsed || mobile) && (
              <>
                <span className="font-medium flex-1">Create New Claim</span>
                <ExternalLink className="w-3.5 h-3.5 opacity-70" />
              </>
            )}
          </a>
        </div>
        
        <div className="space-y-1">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path);
                setMobileMenuOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                isActive(item.path)
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`}
            >
              <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
              {(!sidebarCollapsed || mobile) && <span className="font-medium">{item.label}</span>}
            </button>
          ))}
        </div>
      </nav>

      {/* User Section */}
      {(!sidebarCollapsed || mobile) && accountant && (
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/10">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">
                {accountant.name?.[0]?.toUpperCase() || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{accountant.name}</p>
              <p className="text-xs text-white/50 truncate">{accountant.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full mt-2 flex items-center gap-2 px-3 py-2 text-white/70 hover:bg-white/10 hover:text-white rounded-lg text-sm"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      )}

      {/* Collapse Toggle */}
      {!mobile && (
        <div className="p-3 border-t border-white/10">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-white/50 hover:bg-white/10 hover:text-white rounded-lg transition"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <>
                <ChevronLeft className="w-4 h-4" />
                <span className="text-sm">Collapse</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex flex-shrink-0">
        <Sidebar />
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full">
            <Sidebar mobile />
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="absolute top-4 right-4 p-2 text-white bg-white/20 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-4 lg:px-6 py-3 flex items-center justify-between sticky top-0 z-40">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900 hidden sm:block">
              Accountant Portal
            </h1>
          </div>

          <div className="flex items-center gap-3">
            {/* Notifications */}
            <div className="relative">
              <button 
                onClick={() => setNotificationDropdownOpen(!notificationDropdownOpen)}
                className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <Bell className="w-5 h-5" />
                {notificationCount > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                )}
              </button>
              
              {/* Notification Dropdown */}
              {notificationDropdownOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <h3 className="font-semibold text-gray-900">Notifications</h3>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No new notifications</p>
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <button
                          key={notif.id}
                          onClick={() => {
                            if (notif.path) {
                              navigate(notif.path);
                            } else if (notif.reportId) {
                              navigate(`/accountant/report/${notif.reportId}`);
                            }
                            setNotificationDropdownOpen(false);
                          }}
                          className="w-full px-4 py-3 hover:bg-gray-50 border-b border-gray-50 text-left flex items-start gap-3"
                        >
                          <div className={`p-1.5 rounded-full flex-shrink-0 ${
                            notif.type === 'invite' ? 'bg-blue-100' : 
                            notif.type === 'comment' ? 'bg-green-100' : 'bg-yellow-100'
                          }`}>
                            {notif.type === 'invite' ? (
                              <Mail className="w-4 h-4 text-blue-600" />
                            ) : notif.type === 'comment' ? (
                              <MessageSquare className="w-4 h-4 text-green-600" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-yellow-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">{notif.title}</p>
                            <p className="text-xs text-gray-500 truncate">{notif.message}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(notif.time).toLocaleDateString('en-US', { 
                                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                  {notifications.length > 0 && (
                    <div className="px-4 py-2 border-t border-gray-100">
                      <button 
                        onClick={() => {
                          setNotifications([]);
                          setNotificationCount(0);
                          setNotificationDropdownOpen(false);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-700"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
